/**
 * Permission utility functions for RBAC checks.
 * Server-side enforcement remains authoritative.
 */

import { getDepartmentHeadRoleName } from '../constants/departments';

const hasResolvedPermission = (userProfile, actionKey) =>
  Array.isArray(userProfile?.permissions) && userProfile.permissions.includes(actionKey);

const normalizeEntityId = (entity) => {
  if (entity == null) return null;
  if (typeof entity === 'object') {
    return entity._id != null ? String(entity._id) : null;
  }
  return String(entity);
};

const getUserRoleIds = (userProfile) =>
  (userProfile?.roles || [])
    .map(normalizeEntityId)
    .filter(Boolean);

/**
 * Check permission targets (direct user, assigned roles, membership tier, department).
 * Mirrors server checkPermission / getResolvedPermissions evaluation.
 */
export const userHasPermissionTarget = (userProfile, permission) => {
  if (!permission || !userProfile) return false;

  const userId = normalizeEntityId(userProfile._id ?? userProfile.userId);
  if (
    userId &&
    (permission.users || []).some((id) => normalizeEntityId(id) === userId)
  ) {
    return true;
  }

  const userRoleIds = getUserRoleIds(userProfile);
  if (
    userRoleIds.length > 0 &&
    (permission.roles || []).some((roleId) =>
      userRoleIds.includes(normalizeEntityId(roleId)),
    )
  ) {
    return true;
  }

  if (
    userProfile.membershipType &&
    (permission.tiers || []).includes(userProfile.membershipType)
  ) {
    return true;
  }

  const userDepartmentIds = (userProfile.departments || [])
    .map(normalizeEntityId)
    .filter(Boolean);

  if (
    userDepartmentIds.length > 0 &&
    (permission.departments || []).some((departmentId) =>
      userDepartmentIds.includes(normalizeEntityId(departmentId)),
    )
  ) {
    return true;
  }

  return false;
};

/**
 * Check permission using resolved grants on the profile, or RBAC targets from a catalog.
 */
export const userHasPermissionFromCatalog = (userProfile, actionKey, permissionsCatalog) => {
  if (hasResolvedPermission(userProfile, actionKey)) {
    return true;
  }

  const permission = (permissionsCatalog || []).find(
    (entry) => entry.action_key === actionKey,
  );
  if (permission && userHasPermissionTarget(userProfile, permission)) {
    return true;
  }

  return false;
};

/**
 * Check if the user holds a specific granular RBAC permission.
 * @param {Object} userProfile - The user profile object from Redux state
 * @param {String} actionKey - The permission action_key to check (e.g. 'MANAGE_SEMESTERS')
 * @returns {boolean} True if the user has the permission via resolved RBAC grants
 */
export const hasPermission = (userProfile, actionKey) =>
  hasResolvedPermission(userProfile, actionKey);

/**
 * Check if the user holds any of the given RBAC permissions.
 * @param {Object} userProfile - The user profile object from Redux state
 * @param {String[]} actionKeys - Permission action_keys to check
 * @returns {boolean} True if the user has at least one of the permissions
 */
export const hasAnyPermission = (userProfile, actionKeys) =>
  Array.isArray(actionKeys) && actionKeys.some((key) => hasPermission(userProfile, key));

/**
 * Check if user can manage roles and permissions (access control admin).
 * @param {Object} userProfile - The user profile object from Redux state
 * @returns {boolean} True if user has MANAGE_ACCESS_CONTROL
 */
export const hasManageAccessControlPermission = (userProfile) =>
  hasPermission(userProfile, 'MANAGE_ACCESS_CONTROL');

/**
 * Check if user can manage users (bulk edit, admin user fields).
 * @param {Object} userProfile - The user profile object from Redux state
 * @returns {boolean} True if user has MANAGE_USERS
 */
export const hasManageUsersPermission = (userProfile) =>
  hasPermission(userProfile, 'MANAGE_USERS');

const extractOrganizerId = (item) => {
  if (!item) return null;
  return typeof item === 'object' ? item._id : item;
};

/** Days after event.date that MO/VMO may edit without EDIT_EVENT (3 weeks; matches server). */
export const ORGANIZER_EVENT_EDIT_GRACE_DAYS = 21;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * True while the organizer edit window is open (through grace period after event.date).
 */
export const isWithinOrganizerEditWindow = (eventDate) => {
  if (!eventDate) return false;
  const date = new Date(eventDate);
  if (Number.isNaN(date.getTime())) return false;
  const deadline = date.getTime() + ORGANIZER_EVENT_EDIT_GRACE_DAYS * MS_PER_DAY;
  return Date.now() <= deadline;
};

/**
 * Check if the user is listed as MO or VMO on an event.
 * @param {Object} userProfile
 * @param {Object} event
 * @returns {boolean}
 */
export const isEventOrganizer = (userProfile, event) => {
  if (!userProfile?._id || !event) return false;
  const userId = String(userProfile._id);
  const inList = (list) =>
    Array.isArray(list) &&
    list.some((item) => String(extractOrganizerId(item)) === userId);
  return inList(event.mos) || inList(event.vmos);
};

/**
 * Check if the user can edit an event (EDIT_EVENT, organizer, or
 * MANAGE_GENERAL_MEETING for general meetings).
 */
export const canEditEvent = (userProfile, event) => {
  if (hasPermission(userProfile, 'EDIT_EVENT')) return true;
  if (isEventOrganizer(userProfile, event)) {
    // TEMP: 3-week organizer edit window suspended so MO/VMO can backfill activities links
    // on past events. Restore: return isWithinOrganizerEditWindow(event?.date);
    return true;
  }
  if (event?.isGeneralMeeting && hasPermission(userProfile, 'MANAGE_GENERAL_MEETING')) {
    return true;
  }
  return false;
};

/**
 * Check if the user can delete an event (DELETE_EVENT or organizer within grace period).
 */
export const canDeleteEvent = (userProfile, event) => {
  if (hasPermission(userProfile, 'DELETE_EVENT')) return true;
  if (isEventOrganizer(userProfile, event)) {
    return isWithinOrganizerEditWindow(event?.date);
  }
  return false;
};

/**
 * Check if the user can manage open calls globally or for a specific event they organize.
 * MANAGE_OPENCALLS adds the voting process: close voting, view results, and reopen voting.
 * Organizers can manage their own event's open call during the post-event grace period.
 */
export const canManageOpenCalls = (userProfile, event) => {
  if (hasPermission(userProfile, 'MANAGE_OPENCALLS')) return true;
  if (isEventOrganizer(userProfile, event)) {
    return isWithinOrganizerEditWindow(event?.date);
  }
  return false;
};

/**
 * Create, edit, delete, cancel, and reopen an open call.
 * CREATE_OPENCALL is enough on its own: the member does not need to be MO or VMO.
 * Does not include the voting process or reopening voting.
 */
export const canCreateOpenCall = (userProfile) =>
  hasPermission(userProfile, 'CREATE_OPENCALL');

/**
 * Maintain an open call: create, edit, delete, cancel, and reopen it.
 * Voting results, closing voting, and reopening voting stay on MANAGE_OPENCALLS.
 */
export const canMaintainOpenCall = (userProfile, event) =>
  canCreateOpenCall(userProfile) || canManageOpenCalls(userProfile, event);

/**
 * Open the open-call form for an event.
 */
export const canOpenOpenCallForm = canMaintainOpenCall;

/**
 * Check if the user can vote on open calls.
 */
export const canVoteOpenCall = (userProfile) =>
  hasPermission(userProfile, 'VOTE_OPENCALL');

/**
 * Check if the user can participate in open call voting (vote or manage).
 */
export const canParticipateInOpenCallVoting = (userProfile, permissionsCatalog) =>
  userHasPermissionFromCatalog(userProfile, 'VOTE_OPENCALL', permissionsCatalog) ||
  userHasPermissionFromCatalog(userProfile, 'MANAGE_OPENCALLS', permissionsCatalog);

/**
 * Count users eligible to vote on open calls.
 */
export const countEligibleOpenCallVoters = (users, permissionsCatalog) =>
  (users || []).filter((user) =>
    canParticipateInOpenCallVoting(user, permissionsCatalog),
  ).length;

/**
 * View open call voting results.
 * MANAGE_OPENCALLS only. CREATE_OPENCALL does not grant this, and being MO or VMO does not either.
 */
export const canViewOpenCallResults = (userProfile) =>
  hasPermission(userProfile, 'MANAGE_OPENCALLS');

/**
 * Check if the user can access the admin stats manager.
 */
export const canAccessAdminStats = (userProfile) =>
  hasPermission(userProfile, 'ADMIN_STATS_MANAGER');

/**
 * Check if the user can view and manage general meetings in the events list.
 */
export const canManageGeneralMeetings = (userProfile) =>
  hasPermission(userProfile, 'MANAGE_GENERAL_MEETING');

/**
 * Check if the user can view general meeting attendance on a profile
 * (own profile, or users with MANAGE_GENERAL_MEETING).
 */
export const canViewGeneralMeetingAttendance = (userProfile, targetUserId) => {
  const viewerId = userProfile?._id != null ? String(userProfile._id) : '';
  const targetId = targetUserId != null ? String(targetUserId) : '';
  return viewerId === targetId || canManageGeneralMeetings(userProfile);
};

/**
 * Check if the user can edit any department (global admin permission).
 */
export const canEditDepartments = (userProfile) =>
  hasPermission(userProfile, 'EDIT_DEPARTMENTS');

/**
 * Whether the user holds the RBAC head role for a department.
 * @param {Object} userProfile
 * @param {Object} department
 * @param {Object[]|null|undefined} catalog
 * @returns {boolean}
 */
export const userHasDepartmentRole = (userProfile, department, catalog) => {
  const roleName = getDepartmentHeadRoleName(department, catalog);
  if (!roleName || !Array.isArray(userProfile?.roles) || userProfile.roles.length === 0) {
    return false;
  }
  return userProfile.roles.some((role) => role?.name === roleName);
};

/**
 * Check if the user can view GM attendance on a department page.
 * Allowed for users with MANAGE_GENERAL_MEETING or the head of that department.
 * @param {Object} userProfile
 * @param {Object|null|undefined} department
 * @param {Object[]|null|undefined} catalog
 * @returns {boolean}
 */
export const canViewDepartmentGmAttendance = (userProfile, department, catalog) =>
  canManageGeneralMeetings(userProfile) ||
  userHasDepartmentRole(userProfile, department, catalog);

/**
 * Check if the user can edit a specific department.
 * Allowed for users with EDIT_DEPARTMENTS or the assigned head of that department.
 * @param {Object} userProfile
 * @param {Object|null|undefined} department
 * @param {Object[]|null|undefined} catalog
 * @returns {boolean}
 */
export const canEditDepartment = (userProfile, department, catalog) => {
  if (canEditDepartments(userProfile)) {
    return true;
  }
  if (!department) {
    return false;
  }
  return userHasDepartmentRole(userProfile, department, catalog);
};

/**
 * Department page edit mode: cover image and semester volunteers.
 */
export const canManageDepartmentPage = canEditDepartment;

/**
 * Check if the user can view sensitive user profile fields on any profile.
 */
export const canViewUsersSensitiveData = (userProfile) =>
  hasPermission(userProfile, 'VIEW_USERS_SENSITIVE_DATA');

/**
 * Check if the user can view sensitive fields on a specific user's profile (self-view always allowed).
 */
export const canViewUserSensitiveData = (userProfile, targetUserId) =>
  userProfile?._id === targetUserId || hasPermission(userProfile, 'VIEW_USERS_SENSITIVE_DATA');

/** Contact and address fields (also visible to profile editors with MANAGE_USERS). */
export const canViewUserContactDetails = (userProfile, targetUserId) =>
  userProfile?._id === targetUserId ||
  hasPermission(userProfile, 'VIEW_USERS_SENSITIVE_DATA') ||
  hasPermission(userProfile, 'MANAGE_USERS');

/**
 * Check if the user can edit another user's profile (self-edit always allowed).
 */
export const canEditUserProfile = (userProfile, targetUserId) =>
  userProfile?._id === targetUserId || hasPermission(userProfile, 'MANAGE_USERS');

/**
 * Check if the user can update administrative user fields (suspension, semesters, etc.).
 */
export const canEditUserAdminFields = (userProfile) =>
  hasPermission(userProfile, 'VIEW_USERS_SENSITIVE_DATA');

/**
 * Check if the user can modify loginActive on user records.
 */
export const canEditUserLoginActive = (userProfile) =>
  hasPermission(userProfile, 'MANAGE_ACCESS_CONTROL') ||
  hasPermission(userProfile, 'MANAGE_ROLES_AND_PERMISSIONS');

/** @deprecated Use canEditUserLoginActive */
export const canEditUserAccessLevel = canEditUserLoginActive;

export const FORMER_MEMBERSHIP_TYPES = ['Alumni', 'Unbound'];

export const FORMER_MEMBER_PLATFORM_MESSAGE =
  'You no longer have access to the platform. If you need access, contact the Vice-President of ESN Lisboa.';

/**
 * Alumni and Unbound members keep their profile but cannot use the platform.
 */
export const isFormerMember = (userProfile) =>
  FORMER_MEMBERSHIP_TYPES.includes(userProfile?.membershipType);

/**
 * HR-approved active members only (not waiting list, not Alumni/Unbound).
 */
export const hasPlatformAccess = (userProfile) =>
  userProfile?.loginActive === true && !isFormerMember(userProfile);
